/**
 * Pawn - controllable actor that receives input from a PlayerController.
 * Owns a movement component and optional camera. Delegates input methods to equipped components.
 */

import * as THREE from 'three';

import { isEquipment } from '../components/gameplay/Equipment.js';
import { InteractionComponent } from '../components/gameplay/InteractionComponent.js';
import { DirectionalLightComponent } from '../components/lights/DirectionalLightComponent.js';
import { BasePawnMovementComponent } from '../components/movement/BasePawnMovementComponent.js';
import { SceneComponent } from '../components/SceneComponent.js';
import { NetRuntime } from '../multiplayer/index.js';
import { InputBuffer } from '../multiplayer/InputBuffer.js';
import { NetMovementPredictor } from '../multiplayer/MovementPrediction.js';
import { EngineClass } from '../systems/ClassRegistry.js';
import { ConsoleCommand } from '../systems/ConsoleManager.js';
import { Delegate } from '../utils/Delegate.js';
import { property } from '../utils/serialization/decorator.js';



import { Actor } from './Actor.js';
import { PawnNetTransform, ServerRPC } from './ActorNetworking.js';

import type { ActorOptions } from './Actor.js';
import type { PlayerController } from './PlayerController.js';
import type { IEquipment } from '../components/gameplay/Equipment.js';
import type {
  GamepadAxis,
  GamepadAxisEventDelegate,
  GamepadButton,
  GamepadButtonEventDelegate,
  KeyboardEventDelegate,
  MouseButton,
  MouseEventDelegate
} from '../systems/InputManager.js';

export interface PawnOptions extends ActorOptions {
  // should only be null if the pawn explicitly does not want input based movement logic (VR, etc.)
  movementComponent: BasePawnMovementComponent | null,
}

export type PawnPossessDelegate = Delegate<[Pawn, PlayerController], void>;

@EngineClass('Pawn')
export class Pawn extends Actor {

  private _movementComponent: BasePawnMovementComponent | null = null;
  protected movementPredictor: NetMovementPredictor | null = null;
  protected inputBuffer: InputBuffer | null = null;
  protected lastProcessedInputSequence: number = -1;

  public override get replicated() {
    return super.replicated;
  }

  public override set replicated(value: boolean) {
    super.replicated = value;
    if (this.replicated && this._movementComponent) {
      this._movementComponent.setReplicated(true);
    }
  }

  @property({description: 'The component that the pawn uses to move.'})
  public get movementComponent(): BasePawnMovementComponent | null {
    return this._movementComponent;
  }

  public set movementComponent(component: BasePawnMovementComponent | null) {
    if (this._movementComponent === component) return;
    if (!this.isEditor) {
      if (this._movementComponent) {
        this._movementComponent.removeFromParent();
      }
      if (component) {
        this.rootComponent.add(component);
      }
    }
    if (component && this.replicated) {
      component.setReplicated(true);
    }
    this._movementComponent = component;
  }

  @property({replicate: true, hidden: true, transient: true})
  private playerController: PlayerController | null = null;

  @property({ replicate: true, transient: true, hidden: true })
  protected replicatedInputSequence: number = -1;

  @property({ replicate: true, transient: true, hidden: true })
  protected pawnNetTransform: PawnNetTransform = new PawnNetTransform();

  public readonly onKeyDown: KeyboardEventDelegate = new Delegate<[KeyboardEvent], boolean>();
  public readonly onKeyUp: KeyboardEventDelegate = new Delegate<[KeyboardEvent], boolean>();
  public readonly onMouseDown: MouseEventDelegate = new Delegate<[MouseButton, MouseEvent], boolean>();
  public readonly onMouseUp: MouseEventDelegate = new Delegate<[MouseButton, MouseEvent], boolean>();

  public readonly onGamepadButtonDown: GamepadButtonEventDelegate = new Delegate<[number, GamepadButton, number], boolean>();
  public readonly onGamepadButtonUp: GamepadButtonEventDelegate = new Delegate<[number, GamepadButton, number], boolean>();
  public readonly onGamepadAxisChange: GamepadAxisEventDelegate = new Delegate<[number, GamepadAxis, number], boolean>();
  public readonly onPossessed: PawnPossessDelegate = new Delegate<[Pawn, PlayerController], void>();
  public readonly onUnpossessed: PawnPossessDelegate = new Delegate<[Pawn, PlayerController], void>();

  private directionalLight: DirectionalLightComponent | null = null;

  @property()
  public enableDirectionalLightFollowing: boolean = true;

  public override setRootComponent(newRootComponent: SceneComponent, copyTransform: boolean): this {
    super.setRootComponent(newRootComponent, copyTransform);
    if (this.movementComponent) {
      // move the movement component to the new root
      this.rootComponent.add(this.movementComponent);
    }
    return this;
  }

  public override initialize(options?: PawnOptions): void {
    super.initialize(options);

    this.replicated = true;

    if (options?.movementComponent) {
      this.movementComponent = options.movementComponent;
    }
  }

  public addForwardInput(value: number, isAbsolute: boolean = false) {
    this.movementComponent?.addForwardInput(value, isAbsolute);
  }

  public addRightInput(value: number, isAbsolute: boolean = false) {
    this.movementComponent?.addRightInput(value, isAbsolute);
  }

  public addLookUpInput(value: number, isAbsolute: boolean = false) {
    this.movementComponent?.addLookUpInput(value, isAbsolute);
  }

  public addLookRightInput(value: number, isAbsolute: boolean = false) {
    this.movementComponent?.addLookRightInput(value, isAbsolute);
  }

  public addZoomInput(value: number, isAbsolute: boolean = false) {
    this.movementComponent?.addZoomInput(value, isAbsolute);
  }

  public getInputBuffer(): InputBuffer {
    this.inputBuffer ??= new InputBuffer();
    return this.inputBuffer;
  }

  public getLastProcessedInputSequence(): number {
    return this.lastProcessedInputSequence;
  }

  public setLastProcessedInputSequence(sequence: number): void {
    this.lastProcessedInputSequence = sequence;
  }

  public getMovementPredictor(): NetMovementPredictor | null {
    return this.movementPredictor;
  }

  private getOrCreateMovementPredictor(): NetMovementPredictor {
    this.movementPredictor ??= new NetMovementPredictor();
    return this.movementPredictor;
  }

  //====================================================================================================
  // RPC Methods for Networked Input
  //====================================================================================================

  /**
   * Server RPC for movement input. Client sends input to server for authoritative processing.
   * @param inputSequence - Client's input sequence number for reconciliation
   * @param forwardValue - Forward/backward input [-1, 1]
   * @param rightValue - Right/left input [-1, 1]
   * @param lookUpValue - Look up/down input
   * @param lookRightValue - Look right/left input
   * @param zoomValue - Zoom input [-1, 1]
   * @param isLookAbsolute - Whether look input is absolute (mouse) or speed-based (gamepad)
   */
  @ServerRPC({reliable: false})
  public serverReceiveMovementInput(
    inputSequence: number,
    forwardValue: number,
    rightValue: number,
    lookUpValue: number,
    lookRightValue: number,
    zoomValue: number,
    isLookAbsolute: boolean
  ): void {
    // Store last processed input sequence for client reconciliation
    this.lastProcessedInputSequence = inputSequence;

    // Apply input to movement component (runs only on server)
    this.addForwardInput(forwardValue, false);
    this.addRightInput(rightValue, false);
    this.addLookUpInput(lookUpValue, isLookAbsolute);
    this.addLookRightInput(lookRightValue, isLookAbsolute);
    this.addZoomInput(zoomValue, false);
  }

  @ServerRPC({reliable: true})
  public serverReceiveJump(strength: number = 1): void {
    this.jump(strength);
  }

  @ServerRPC({reliable: true})
  public serverReceiveStopJump(): void {
    this.stopJump();
  }

  //====================================================================================================

  public override getPlayerController(): PlayerController | null {
    return this.playerController;
  }

  /**
   * Handles jump input with client-side prediction and server replication
   * @param strength - Jump strength
   */
  public handleJump(strength: number = 1): void {
    this.jump(strength);
    if (this.isLocalAutonomousProxy()) {
      this.serverReceiveJump(strength);
    }
  }

  /**
   * Handles stop jump input with client-side prediction and server replication
   */
  public handleStopJump(): void {
    this.stopJump();
    if (this.isLocalAutonomousProxy()) {
      this.serverReceiveStopJump();
    }
  }

  public jump(strength: number = 1) {
    this.movementComponent?.jump(strength);
  }

  public stopJump() {
    this.movementComponent?.stopJump();
  }

  public beginFire(): boolean {
    return this.callEquipment('beginFire');
  }

  public endFire(): boolean {
    return this.callEquipment('endFire');
  }

  public beginAltFire(): boolean {
    return this.callEquipment('beginAltFire');
  }

  public endAltFire(): boolean {
    return this.callEquipment('endAltFire');
  }

  public reload(): boolean {
    return this.callEquipment('reload');
  }

  /**
   * Called by PlayerController when the interaction key is pressed.
   * Forwards to the InteractionComponent to handle the interaction.
   * @returns True if an interaction was started, false otherwise
   */
  public beginInteract(): boolean {
    return this.getInteractionComponent()?.beginInteract() ?? false;
  }

  /**
   * Called by PlayerController when the interaction key is released.
   * Forwards to the InteractionComponent to end any active interaction.
   */
  public endInteract(): void {
    this.getInteractionComponent()?.endInteract();
  }

  /**
   * Gets the InteractionComponent attached to this pawn.
   * @returns The InteractionComponent or null if not found
   */
  public getInteractionComponent(): InteractionComponent | null {
    return this.rootComponent.getComponent(InteractionComponent);
  }

  public override setWorldTransform(transform: {
    position?: THREE.Vector3,
    rotation?: THREE.Euler,
    scale?: THREE.Vector3
  }): void {
    if (this.movementComponent) {
      this.movementComponent.setPawnWorldTransform(transform);
    } else {
      super.setWorldTransform(transform);
    }
  }

  public handleKeyDown(e: KeyboardEvent): boolean {
    return this.onKeyDown.invokeWithPredicate(result => result, e).at(-1) ?? false;
  }

  public handleKeyUp(e: KeyboardEvent): boolean {
    return this.onKeyUp.invokeWithPredicate(result => result, e).at(-1) ?? false;
  }

  public handleMouseDown(button: MouseButton, e: MouseEvent): boolean {
    return this.onMouseDown.invokeWithPredicate(result => result, button, e).at(-1) ?? false;
  }

  public handleMouseUp(button: MouseButton, e: MouseEvent): boolean {
    return this.onMouseUp.invokeWithPredicate(result => result, button, e).at(-1) ?? false;
  }

  /**
   * Handles mouse wheel events. Can be overridden by subclasses to implement zoom/scrolling.
   * @param e - The wheel event
   * @returns True if the event was handled, false otherwise
   */
  public handleMouseWheel(e: WheelEvent): boolean {
    if (this.movementComponent?.handleMouseWheel(e)) {
      return true;
    }
    return false;
  }

  public handleGamepadButtonDown(gamepadIndex: number, buttonIndex: GamepadButton, value: number): boolean {
    return this.onGamepadButtonDown.invokeWithPredicate(result => result, gamepadIndex, buttonIndex, value).at(-1) ?? false;
  }

  public handleGamepadButtonUp(gamepadIndex: number, buttonIndex: GamepadButton): boolean {
    return this.onGamepadButtonUp.invokeWithPredicate(result => result, gamepadIndex, buttonIndex, 0).at(-1) ?? false;
  }

  public handleGamepadAxisChange(gamepadIndex: number, axisIndex: GamepadAxis, value: number): boolean {
    return this.onGamepadAxisChange.invokeWithPredicate(result => result, gamepadIndex, axisIndex, value).at(-1) ?? false;
  }

  public override isNetRelevantFor(clientId?: string, clientData?: {
    position?: THREE.Vector3Like;
    [key: string]: any
  }): boolean {
    return this.playerController !== null || super.isNetRelevantFor(clientId, clientData);
  }

  public onRepPawnNetTransform(): void {
    if (this.isSimulatedProxy() && this.isPhysicsEnabled()) {
      this.setPhysicsEnabled(false);
    }
    this.getOrCreateMovementPredictor().addRemoteTransform({
      timestamp: this.pawnNetTransform.timestamp || performance.now(),
      position: this.pawnNetTransform.position.clone(),
      rotation: new THREE.Quaternion().setFromEuler(this.pawnNetTransform.rotation),
      scale: this.pawnNetTransform.scale.clone(),
    });
  }

  public onRepReplicatedInputSequence(): void {
    if (!this.isLocalAutonomousProxy() || this.replicatedInputSequence < 0) return;
    this.reconcilePrediction(
      this.replicatedInputSequence,
      this.pawnNetTransform.position.toArray() as [number, number, number],
      this.pawnNetTransform.rotation.toArray() as [number, number, number],
      this.pawnNetTransform.scale.toArray() as [number, number, number],
    );
  }

  public override tickPostPhysics(deltaTime: number): void {
    super.tickPostPhysics(deltaTime);
    if (!this.isSimulatedProxy()) {
      this.pawnNetTransform.position.copy(this.rootComponent.getWorldPosition());
      this.pawnNetTransform.rotation.copy(this.rootComponent.getWorldRotation());
      this.pawnNetTransform.scale.copy(this.rootComponent.getWorldScale());
      this.pawnNetTransform.timestamp = NetMovementPredictor.currentTime();
      this.replicatedInputSequence = this.lastProcessedInputSequence;
    }
  }

  /**
   * Reconciles client-side prediction with server state
   * @param lastProcessedInputSequence - The last input sequence processed by the server
   * @param serverPosition - The authoritative server position
   * @param serverRotation - The authoritative server rotation
   * @param serverScale - The authoritative server scale
   */
  protected reconcilePrediction(
    lastProcessedInputSequence: number,
    serverPosition: [number, number, number],
    serverRotation: [number, number, number],
    serverScale: [number, number, number]
  ): void {
    // Update last processed sequence
    this.lastProcessedInputSequence = lastProcessedInputSequence;

    // Acknowledge inputs up to this sequence
    if (this.inputBuffer) {
      this.inputBuffer.acknowledgeInputs(lastProcessedInputSequence);

      // Rewind to server state
      this.rootComponent.position.set(serverPosition[0], serverPosition[1], serverPosition[2]);
      this.rootComponent.rotation.set(serverRotation[0], serverRotation[1], serverRotation[2]);
      this.rootComponent.scale.set(serverScale[0], serverScale[1], serverScale[2]);

      // Replay unacknowledged inputs
      const unacknowledgedInputs = this.inputBuffer.getUnacknowledgedInputs();
      if (unacknowledgedInputs.length > 0) {
        // TODO: Replay inputs through movement component
        // For now, just log that we would replay
        console.debug(`[Pawn] Would replay ${unacknowledgedInputs.length} unacknowledged inputs at seq: ${lastProcessedInputSequence} for ${this.name}`);
      }
    }
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);

    // make the directional light follow the pawn when it's possessed
    if (this.enableDirectionalLightFollowing && this.getPlayerController()) {
      if (!this.directionalLight) {
        const actors = this.getWorld()?.getActors() ?? [];
        for (const actor of actors) {
          this.directionalLight = actor.getComponent(DirectionalLightComponent);
          if (this.directionalLight) {
            break;
          }
        }
      }

      if (this.directionalLight) {
        this.directionalLight.moveTargetTo(this.getWorldPosition());
      }
    }
  }

  //====================================================================================================

  protected callEquipment(method: keyof IEquipment): boolean {
    for (const component of this.getComponents(SceneComponent)) {
      if (isEquipment(component) && component[method]()) {
        return true;
      }
    }
    return false;
  }

  protected override doBeginPlay(): void {
    super.doBeginPlay();
    this.getWorld()?.consoleManager.addCommandHandler(this);

    // Auto-create InteractionComponent if not already present
    if (!this.rootComponent.getComponent(InteractionComponent)) {
      this.rootComponent.add(InteractionComponent.create({}));
    }
  }

  protected override doEndPlay(): void {
    super.doEndPlay();
    this.getWorld()?.consoleManager.removeCommandHandler(this);
  }

  @ConsoleCommand('jump', 'Make the pawn jump (usage: jump [strength])')
  private jumpCommand(strength: string = '1'): string {
    this.jump(Number(strength));
    return 'Jumped';
  }

  public _privateSetPlayerController(playerController: PlayerController | null) {
    if (this.playerController === playerController) {
      return;
    }

    if (this.playerController) {
      this.onUnpossessed.invoke(this, this.playerController);
    }

    this.playerController = playerController;

    if (this.playerController) {
      this.onPossessed.invoke(this, this.playerController);
      if (!NetRuntime.isClient()) {
        this.replicated = true;
        this.setAutonomousProxy(true);
      }
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_Pawn';
  }
}
